import React, { FunctionComponent, useContext } from 'react'
import { Field, Form, FormSpy } from 'react-final-form'
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { LYRA_CONFIDENTIALITY_MSG } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { genericErrorMessage } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import { ALREADY_HAVE_AN_ACCOUNT_MSG } from '@lyrahealth-inc/shared-app-logic/src/features/registration/messages'
import { validateEmailForm } from '@lyrahealth-inc/shared-app-logic/src/features/registration/utils'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { FormContainer } from '../../atoms/formElements/formUtils'
import { InputFieldRFF } from '../../atoms/formElements/InputField'
import { RegistrationEmailCaptureAnimation } from '../../atoms/illustrations/RegistrationEmailCaptureAnimatedIllustration'
import { Link } from '../../atoms/link/Link'
import { OrDivider } from '../../atoms/orDivider/OrDivider'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { SSO_BUTTON_MESSAGE_TYPE, SSO_BUTTON_TYPE, SsoButton } from '../../atoms/ssoButton/SsoButton'
import { Subhead } from '../../atoms/subhead/Subhead'
import { TOOLTIP_TRIGGER_ICON } from '../../atoms/tooltip/Tooltip'
import { IS_WEB } from '../../constants'
import { AppContext } from '../../context'
import { useMediaQuerySize } from '../../hooks/useMediaQuerySize'
import { ConfidentialityNotice } from '../../molecules/confidentialityNotice/ConfidentialityNotice'
import { InlineErrorBanner } from '../../molecules/inlineErrorBanner/InlineErrorBanner'
import { InputAccessory } from '../../molecules/inputAccessory/InputAccessory'
import { NavBar } from '../../molecules/navBar/NavBar'
import { getAuroraAwareBackgroundColor } from '../../styles/commonStyles'
import { SubheadSize } from '../../styles/typeStyles'
import { StickyView } from '../../templates/stickyView/StickyView'
import { TwoColumnRegistrationLayout } from '../../templates/twoColumnLayout/TwoColumnRegistrationLayout'
import { openUrl } from '../../utils/expo-linking'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { LegalAndCopyrightFooter } from '../legalAndCopyrightFooter/LegalAndCopyrightFooter'
import { RegistrationValueProps } from '../registrationValueProps/RegistrationValueProps'

export interface EmailCaptureFormValues {
  emailAddress: string
}

const EmailFormContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.breakpoints.isMinWidthTablet
    ? 0
    : `${theme.spacing['0px']} ${theme.spacing['16px']} ${theme.spacing['16px']}`,
  ...(!IS_WEB && {
    width: '100%',
  }),
}))

const HeaderContainer = styled(Subhead)<{ theme: ThemeType; isMobileSized: boolean }>(({ theme, isMobileSized }) => ({
  marginBottom: theme.spacing['24px'],
  ...(!isMobileSized && { marginRight: theme.spacing['72px'] }),
  ...(isMobileSized && { textAlign: 'center' }),
}))

const FooterTextContainer = styled.View({
  width: '100%',
  alignItems: 'center',
})

const ButtonGroupContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing['8px'],
}))

const ButtonContainer = styled.View<{ theme: ThemeType; addMarginBottom?: boolean }>(({ theme, addMarginBottom }) => ({
  width: '100%',
  ...(addMarginBottom && {
    marginBottom: theme.spacing['16px'],
  }),
}))

const AlreadyHaveAccountText = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  color: theme.colors.textSecondary,
}))

const LegalFooterContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  marginTop: spacing['24px'],
}))

const ConfidentialityContainer = styled.View<{ theme: ThemeType; isMobileSized: boolean }>(
  ({ theme: { spacing }, isMobileSized }) => ({
    ...(!isMobileSized && {
      marginTop: spacing['48px'],
    }),
  }),
)

const RecaptchaContainer = styled.View<{ theme: ThemeType; lessMargin: boolean }>(
  ({ theme: { breakpoints, spacing }, lessMargin }) => ({
    ...(breakpoints.isMinWidthLaptop
      ? {
          marginTop: lessMargin ? '100px' : '140px',
        }
      : { marginTop: spacing['16px'] }),
  }),
)

export interface RegistrationEmailCaptureFormV2Props {
  onSignUpButtonPress: (values: EmailCaptureFormValues) => void
  onSignInLinkPress: () => void
  onBackButtonPress?: () => void
  appleSSOAvailable?: boolean
  googleSSOAvailable?: boolean
  onAppleSignUpButtonPress?: () => void
  onGoogleSignUpButtonPress?: () => void
  isGenerateVerificationTokenLoading?: boolean
  customAppleSSOContent?: React.ReactNode
  customGoogleSSOContent?: React.ReactNode
  reCaptchaContent?: React.ReactNode
  isEmailError?: boolean
  emailErrorMessage?: MessageDescriptor
  customerName?: string
  isInternationalRegistrationExperience?: boolean
  hideDependentField?: boolean
  libraryDisabled?: boolean
  emailValue?: string
  onEmailChange?: (value: string) => void
  isUserGoogleCriticalTalent: boolean
}

export const RegistrationEmailCaptureFormV2: FunctionComponent<RegistrationEmailCaptureFormV2Props> = ({
  appleSSOAvailable,
  googleSSOAvailable,
  onAppleSignUpButtonPress = noop,
  onGoogleSignUpButtonPress = noop,
  onBackButtonPress,
  onSignInLinkPress,
  onSignUpButtonPress,
  isGenerateVerificationTokenLoading,
  customAppleSSOContent,
  customGoogleSSOContent,
  reCaptchaContent,
  isEmailError,
  emailErrorMessage,
  isInternationalRegistrationExperience,
  hideDependentField = false,
  libraryDisabled = false,
  emailValue,
  onEmailChange,
  isUserGoogleCriticalTalent,
}) => {
  const name = 'RegistrationEmailCaptureForm'
  const isSsoAvailable = appleSSOAvailable || googleSSOAvailable || customAppleSSOContent || customGoogleSSOContent
  const { formatMessage } = useIntl()
  const { isMobileSized, isMinWidthLaptop, isMinWidthDesktop } = useMediaQuerySize()
  const { colors, isLyraSelect, isDarkMode, isAuroraTheme } = useTheme() as ThemeType
  const isLyraSelectAndDarkMode = isLyraSelect && isDarkMode
  const { emailCapturePageExperimentV2 } = useContext(AppContext)
  const isEmailCaptureExperimentB = !isLyraSelect && emailCapturePageExperimentV2 === 'Experiment B'

  const confidentialityNotice = () => {
    return (
      <ConfidentialityContainer isMobileSized={isMobileSized}>
        <ConfidentialityNotice
          customStyle={isMobileSized ? { marginBottom: 0 } : undefined}
          text={<FormattedMessage {...LYRA_CONFIDENTIALITY_MSG} />}
          accessibilityLabel={formatMessage(LYRA_CONFIDENTIALITY_MSG)}
        />
      </ConfidentialityContainer>
    )
  }

  const footerLinkPressed = (url: string) => {
    openUrl(url)
  }

  const CopyrightFooter = (
    <LegalFooterContainer>
      <LegalAndCopyrightFooter
        footerLinkPressed={footerLinkPressed}
        isInternational={isInternationalRegistrationExperience}
        removeSpacing
      />
    </LegalFooterContainer>
  )

  const body = (
    <>
      <EmailFormContainer testID={tID(`${name}-formContainer`)}>
        <Form
          onSubmit={onSignUpButtonPress}
          initialValues={{ emailAddress: emailValue }}
          validate={(formValues) => validateEmailForm(formValues, formatMessage)}
          render={({ handleSubmit, errors, touched }) => (
            <FormContainer handleSubmit={handleSubmit}>
              {onEmailChange && (
                <FormSpy
                  subscription={{ values: true }}
                  onChange={({ values }) => {
                    onEmailChange(values.emailAddress)
                  }}
                />
              )}
              <HeaderContainer
                isMobileSized={isMobileSized}
                size={SubheadSize.LARGE}
                text={
                  <FormattedMessage
                    defaultMessage='Sign up to activate your benefit'
                    description='A title indicating that a user can set up an account with this form'
                  />
                }
              />
              {isEmailError && <InlineErrorBanner text={formatMessage(emailErrorMessage || genericErrorMessage)} />}
              <Field
                testID={`${name}-emailAddressField`}
                name='emailAddress'
                label={formatMessage({
                  defaultMessage: 'Personal email',
                  description: 'Label of field for the user to enter their personal email address',
                })}
                component={InputFieldRFF}
                toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
                toolTipContentStyle={{ container: { maxWidth: 243 } }}
                baseInputStyle={{ marginBottom: 16 }}
                inputContainerStyle={{ borderRadius: 8 }}
                inputAccessoryViewID={name}
                returnKeyType='done'
                autoComplete='email'
                textContentType='emailAddress'
                autoCapitalize='none'
                error={touched && errors?.emailAddress}
              />
              <ButtonGroupContainer>
                <PrimaryButton
                  loading={!!isGenerateVerificationTokenLoading}
                  fullWidth
                  testID={tID(`${name}-defaultSignupButton`)}
                  text={formatMessage({
                    defaultMessage: 'Claim your benefit',
                    description: 'Button that allows user to sign up with their email',
                  })}
                  onPress={handleSubmit}
                />
                {isSsoAvailable && <OrDivider />}
                {googleSSOAvailable && (
                  <ButtonContainer addMarginBottom={appleSSOAvailable}>
                    <SsoButton
                      testId={tID('Registration-GoogleButton')}
                      type={SSO_BUTTON_TYPE.GOOGLE}
                      messageType={SSO_BUTTON_MESSAGE_TYPE.SIGN_UP}
                      onPress={onGoogleSignUpButtonPress}
                    />
                  </ButtonContainer>
                )}
                {customGoogleSSOContent}
                {appleSSOAvailable && (
                  <ButtonContainer>
                    <SsoButton
                      testId={tID('Registration-AppleButton')}
                      type={SSO_BUTTON_TYPE.APPLE}
                      messageType={SSO_BUTTON_MESSAGE_TYPE.SIGN_UP}
                      onPress={onAppleSignUpButtonPress}
                    />
                  </ButtonContainer>
                )}
                {customAppleSSOContent}
              </ButtonGroupContainer>
            </FormContainer>
          )}
        />
      </EmailFormContainer>
      <FooterTextContainer>
        <AlreadyHaveAccountText
          text={
            <FormattedMessage
              {...ALREADY_HAVE_AN_ACCOUNT_MSG}
              values={{
                link: (text: string) => (
                  <Link testID={tID(`${name}-signInLink`)} onPress={onSignInLinkPress} text={text} underline />
                ),
              }}
            />
          }
        />
      </FooterTextContainer>
      {confidentialityNotice()}
      {isMinWidthLaptop && <RecaptchaContainer lessMargin={!!appleSSOAvailable}>{reCaptchaContent}</RecaptchaContainer>}
      {isMinWidthLaptop && CopyrightFooter}
    </>
  )

  const sideContent = () => {
    return (
      <>
        {isEmailCaptureExperimentB ? (
          <>
            {isMinWidthLaptop && (
              <RegistrationEmailCaptureAnimation width={isMinWidthDesktop ? 500 : 400} style={{ margin: 'auto' }} />
            )}
            <RegistrationValueProps
              isCondensedVersion
              hideDependentField={hideDependentField}
              libraryDisabled={libraryDisabled}
              isUserGoogleCriticalTalent={isUserGoogleCriticalTalent}
            />
            {!isMinWidthLaptop && <RegistrationEmailCaptureAnimation width={320} style={{ margin: 'auto' }} />}
          </>
        ) : (
          <RegistrationValueProps
            hideDependentField={hideDependentField}
            libraryDisabled={libraryDisabled}
            isUserGoogleCriticalTalent={isUserGoogleCriticalTalent}
          />
        )}
        {!isMinWidthLaptop && <RecaptchaContainer lessMargin={isMobileSized}>{reCaptchaContent}</RecaptchaContainer>}
        {!isMinWidthLaptop && CopyrightFooter}
      </>
    )
  }

  return (
    <>
      <StickyView
        viewBackgroundColor={getAuroraAwareBackgroundColor(
          isAuroraTheme,
          colors.components.twoColumnRegistrationLayout.formBackground.background,
        )}
        headerComponent={
          !!onBackButtonPress && (
            <NavBar showBackButton onBack={onBackButtonPress} noBottomBorder={isLyraSelectAndDarkMode} isTransparent />
          )
        }
        safeAreaInsets={{ bottom: 0 }}
      >
        <TwoColumnRegistrationLayout
          mainContent={body}
          sideContent={sideContent()}
          isMainContentCentered={false}
          showSideContent
          {...(isEmailCaptureExperimentB && { sideContentBackgroundColor: colors.backgroundSecondary })}
        />
      </StickyView>
      <InputAccessory nativeID={name} />
    </>
  )
}
